import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useApiBase } from '../hooks/useApiBase';
import { useAuth } from './AuthContext';
import { getConversations, getConversationMessages, sendMessage } from '../api/activities';
import type { Conversation, ConversationMessagesResponse } from '../types/messages';
import type { Activity } from '../types/activities';
import { useLogEvent } from '../hooks/useLogEvent';
import { isAbortError } from '../lib/apiErrorHandling';

interface MessagesContextValue {
  // Badge data (always available)
  unreadCount: number;
  
  // Full list data (only loaded when needed)
  conversations: Conversation[];
  currentConversation: ConversationMessagesResponse | null;
  loading: boolean;
  error: string | null;
  
  // Methods
  fetchConversations: (showLoading?: boolean) => void;
  fetchConversationMessages: (conversationId: string) => Promise<void>;
  sendMessage: (recipients: string[], text: string, conversationId?: string) => Promise<Activity | null>;
  setCurrentConversation: (conversation: ConversationMessagesResponse | null) => void;
  
  // Lazy loading control
  requestFullList: () => void;
  releaseFullList: () => void;
}

const MessagesContext = createContext<MessagesContextValue | null>(null);

export function MessagesProvider({ children }: { children: React.ReactNode }) {
  const apiBase = useApiBase();
  const { user, sessionToken } = useAuth();
  const logger = useLogEvent('messages');
  const POLL_INTERVAL_MS = 120000;
  const sessionGenerationRef = useRef(0);
  const sessionRef = useRef(sessionToken);
  const conversationSessionRef = useRef(sessionToken);
  if (sessionRef.current !== sessionToken) {
    sessionRef.current = sessionToken;
    sessionGenerationRef.current += 1;
  }
  const conversationSeq = useRef(0);
  const conversationAbort = useRef<AbortController | null>(null);
  const requestedConversationId = useRef<string | null>(null);
  const loggerRef = useRef(logger);
  loggerRef.current = logger;

  useEffect(() => {
    // Child page effects may already have started a request for this session.
    if (conversationSessionRef.current !== sessionToken) {
      conversationSeq.current += 1;
      conversationAbort.current?.abort();
      requestedConversationId.current = null;
      setConversationData(null);
      setConversationLoading(false);
      setConversationError(null);
    }
    return () => {
      if (conversationSessionRef.current === sessionToken) conversationAbort.current?.abort();
    };
  }, [sessionToken]);
  
  // Badge state (always loaded)
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Full list state (lazy loaded)
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setConversationData] = useState<ConversationMessagesResponse | null>(null);
  const [listLoading, setLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [listError, setError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const loading = listLoading || conversationLoading;
  const error = conversationError || listError;
  const setCurrentConversation = useCallback((conversation: ConversationMessagesResponse | null) => {
    conversationSeq.current += 1;
    conversationAbort.current?.abort();
    requestedConversationId.current = conversation?.conversation_id ?? null;
    setConversationData(conversation);
    setConversationLoading(false);
    setConversationError(null);
  }, []);
  
  // Track which components are using full list
  const fullListSubscribersRef = useRef<number>(0);
  const [hasSubscribers, setHasSubscribers] = useState(false);

  const fetchConversations = useCallback(async (showLoading = true) => {
    const generation = sessionGenerationRef.current;
    if (!user?.id || !sessionToken) {
      setConversations([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await getConversations(apiBase);
      if (generation !== sessionGenerationRef.current) {
        return;
      }
      setConversations(response.conversations);
      // Calculate unread count from conversations
      const totalUnread = response.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadCount(totalUnread);
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      if (generation !== sessionGenerationRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to load conversations';
      setError(message);
      logger.logError('Failed to load conversations', {
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
    } finally {
      if (showLoading) {
        if (generation === sessionGenerationRef.current) {
          setLoading(false);
        }
      }
    }
  }, [apiBase, user?.id, sessionToken, logger]);

  const fetchConversationMessages = useCallback(async (conversationId: string) => {
    const generation = sessionGenerationRef.current;
    conversationSessionRef.current = sessionToken;
    conversationAbort.current?.abort();
    const controller = new AbortController();
    conversationAbort.current = controller;
    const seq = ++conversationSeq.current;
    requestedConversationId.current = conversationId;
    const ownsRequest = () => !controller.signal.aborted && generation === sessionGenerationRef.current && seq === conversationSeq.current;
    setConversationData(null);
    setConversationError(null);
    if (!sessionToken) { setConversationLoading(false); return; }
    setConversationLoading(true);
    try {
      const response = await getConversationMessages(apiBase, conversationId, { signal: controller.signal });
      if (!ownsRequest()) return;
      setConversationData(response);
    } catch (err) {
      if (!ownsRequest() || isAbortError(err)) return;
      const message = err instanceof Error ? err.message : 'Failed to load conversation messages';
      setConversationError(message);
      loggerRef.current.logError('Failed to load conversation messages', { conversation_id: conversationId, error: message });
    } finally {
      if (ownsRequest()) setConversationLoading(false);
    }
  }, [apiBase, sessionToken]);

  const sendMessageToConversation = useCallback(
    async (recipients: string[], text: string, conversationId?: string): Promise<Activity | null> => {
      const generation = sessionGenerationRef.current;
      if (!sessionToken) {
        throw new Error('Cannot send message without an active session.');
      }

      try {
        const message = await sendMessage(apiBase, recipients, text, conversationId);
        if (generation !== sessionGenerationRef.current) {
          return null;
        }
        
        // Refresh conversations silently (no loading state) and current conversation
        await fetchConversations(false);
        if (conversationId && requestedConversationId.current === conversationId) {
          await fetchConversationMessages(conversationId);
        }
        
        return message;
      } catch (err) {
        if (generation !== sessionGenerationRef.current) {
          return null;
        }
        logger.logError('Failed to send message', {
          recipients,
          conversation_id: conversationId,
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    [apiBase, fetchConversations, fetchConversationMessages, logger, sessionToken]
  );

  // Load conversations when subscribers exist
  useEffect(() => {
    if (hasSubscribers && user?.id && sessionToken) {
      fetchConversations(true);
    }
  }, [hasSubscribers, user?.id, sessionToken, fetchConversations]);

  // Background polling (silent refresh) while subscribers are active
  // Use ref to avoid restarting interval when fetchConversations recreates
  const fetchConversationsRef = useRef(fetchConversations);
  useEffect(() => {
    fetchConversationsRef.current = fetchConversations;
  }, [fetchConversations]);

  useEffect(() => {
    if (!hasSubscribers || !user?.id || !sessionToken) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      fetchConversationsRef.current(false);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasSubscribers, user?.id, sessionToken]);

  const requestFullList = useCallback(() => {
    fullListSubscribersRef.current += 1;
    setHasSubscribers(true);
  }, []);

  const releaseFullList = useCallback(() => {
    fullListSubscribersRef.current = Math.max(0, fullListSubscribersRef.current - 1);
    if (fullListSubscribersRef.current === 0) {
      setHasSubscribers(false);
    }
  }, []);

  const value: MessagesContextValue = React.useMemo(() => ({
    unreadCount,
    conversations,
    currentConversation,
    loading,
    error,
    fetchConversations: (showLoading = true) => fetchConversations(showLoading),
    fetchConversationMessages,
    sendMessage: sendMessageToConversation,
    setCurrentConversation: setCurrentConversation,
    requestFullList,
    releaseFullList,
  }), [unreadCount, conversations, currentConversation, loading, error, fetchConversations, fetchConversationMessages, sendMessageToConversation, requestFullList, releaseFullList, setCurrentConversation]);

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessagesContext(): MessagesContextValue {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessagesContext must be used within MessagesProvider');
  }
  return context;
}
